import {ChangeDetectorRef, Component, OnDestroy, OnInit, TrackByFunction} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {MatDialog} from '@angular/material/dialog';
import {Compbaser} from '../compbaser';
import {EnumSizeImageType} from '../dialog-get-asset/dialog-get-asset.component';
import {actionAddResources, actionGetAIImage, actionGetAIImageReply, actionGetAIImages, actionGetAIImagesReply, actionGetAIVideo, actionGetAIVideoReply} from '../../store/actions/user.actions';
import {AppService, LocalStoreService, StoreSelectorsService} from '../../services';
import {EnumWizardUpgradeToasts, FileObj, ICRUDRecord, IResource, IUser, UploadStatus} from '../../interfaces';
import {ToastrService} from 'ngx-toastr';
import {convertToFileFromImageUrlOrBase64, formatBytes, isHostDevOrLocal, isHostLocal, TOAST_FASTER} from '../../common';
import {ofType} from '@ngrx/effects';
import {ActionsSubject} from '@ngrx/store';
import {Lib} from '../../../libs/Lib';
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import * as _ from 'lodash';

interface IAIImage {
    name: string;
    url: string;
    base64: string;
    loading: boolean;
}

@Component({
    selector: 'app-resources-ai',
    templateUrl: './resources-ai.html',
    styleUrls: ['./resources-ai.scss'],
})
export class ResourcesAi extends Compbaser implements OnInit, OnDestroy { // Implement OnDestroy if Compbaser doesn't handle all cleanup
    user: IUser;
    totalCalls = 5;
    callsToday = 0; // Number of calls made today
    safeVideoUrl: SafeResourceUrl | null = null;
    videoUrl: string | null = null; // Store the raw video URL
    filter = '';
    readonly filter1 = 'dogs eating banana';
    readonly filter2 = 'a realistic video of cat running through hot lava';
    // readonly filter2 = 'a video of a a kid running on the beach holding a balloon';
    videoUploadReady = false;
    m_numSizeImageType = EnumSizeImageType; // Assuming this is used elsewhere in the template
    m_enumMediaTypeSelected = '1024x1024'; // Default image size

    loadingImages = false; // General loading indicator for API calls
    aiImages: IAIImage[] = []; // Array to hold generated image data
    selectedMediaType: 'image' | 'video' = 'image';
    private callDate: Date | null = null; // Last date an AI call was made (component state)

    private processedFileIds = new Set<string>(); // Track IDs of files already processed for resource creation

    constructor(
        private sanitizer: DomSanitizer,
        public storeSelector: StoreSelectorsService,
        private localStore: LocalStoreService,
        private toastr: ToastrService,
        private cd: ChangeDetectorRef,
        private appService: AppService,
        private actionsSubj: ActionsSubject,
        private translocoService: TranslocoService,
        public override dialog: MatDialog // If Compbaser provides dialog
    ) {
        super(dialog); // Call base class constructor
        this.cod( // Assuming `cod` is from Compbaser for managing subscriptions
            this.storeSelector.getUser().subscribe((i_user: IUser | null) => {
                if (i_user) { // Handle potential null user
                    this.user = i_user;
                }
            })
        );
    }

    ngOnInit() {
        this.filter = this.filter1; // Set initial filter based on default media type

        if (isHostLocal()) this.totalCalls = 100;
        this.loadCallLimitState();

        // --- Subscribe to NGRX Actions ---
        this.subscribeImagesReply();
        this.subscribeToDalleImageBlobReply();
        this.subscribeToVeroVideoReply();
        this.subscribeToUploadCompletion();
    }

    // --- Initialization Helper ---
    private loadCallLimitState(): void {
        const storedDateStr = this.localStore.getStorageValConfig('resourceAICallDate');
        const storedCallsStr = this.localStore.getStorageValConfig('resourceAICalls');

        if (storedDateStr) {
            const storedDate = new Date(storedDateStr);
            if (!isNaN(storedDate.getTime())) { // Check if the stored date is valid
                this.callDate = storedDate;
                const today = new Date();
                // Check if the stored date is actually today
                if (
                    this.callDate.getFullYear() === today.getFullYear() &&
                    this.callDate.getMonth() === today.getMonth() &&
                    this.callDate.getDate() === today.getDate()
                ) {
                    // If it's today, parse the stored calls
                    const parsedCalls = parseInt(storedCallsStr || '0', 10);
                    this.callsToday = isNaN(parsedCalls) ? 0 : parsedCalls; // Default to 0 if parsing fails
                } else {
                    // Stored date is from a previous day, reset counter
                    this.callsToday = 0;
                    // Note: The checkAICallLimit function will handle resetting the date in localStorage later
                }
            } else {
                // Invalid date string stored, reset state
                console.warn("Invalid date found in localStorage ('resourceAICallDate'). Resetting state.");
                this.callDate = null;
                this.callsToday = 0;
            }
        } else {
            // No date stored, assume first use or cleared storage
            this.callDate = null;
            this.callsToday = 0;
        }
        console.log(`Initial AI Call State: Date=${this.callDate?.toISOString()}, Calls Today=${this.callsToday}`);
    }


    // --- NGRX Action Subscriptions ---

    private subscribeImagesReply(): void {
        this.cod(
            this.actionsSubj
                .pipe(ofType(actionGetAIImagesReply))
                .subscribe((reply) => {
                    this.loadingImages = false; // Stop loading indicator
                    if (reply?.payload?.error) {
                        this.loadingImages = false;
                        this.toastr.warning(reply.payload.error);
                        this.cd.markForCheck();
                        return;

                    }
                    if (_.isEmpty(reply.payload)) {
                        this.loadingImages = false;
                        this.toastr.warning(this.translocoService.translate('general.pleaseUpgrade3', {}, this.translocoService.getActiveLang()));
                        this.cd.markForCheck();
                        return;
                    }
                    // Check for single image response format first
                    if (reply.payload?.imageData && reply.payload?.success && reply.payload.imageData.length > 0) {
                        const mimeType = reply.payload.mimeType || 'image/png';
                        const extension = mimeType.split('/')[1] || 'png';
                        this.aiImages = [{
                            name: `ai_image_${Date.now()}.${extension}`,
                            url: '',
                            base64: `data:${mimeType};base64,${reply.payload.imageData}`,
                            loading: false
                        }];
                        this.loadingImages = false;
                        this.cd.markForCheck();
                        return;
                    }

                    // Validate the payload structure for array format
                    if (!Array.isArray(reply.payload) || reply.payload.length === 0) {
                        this.toastr.warning('AI ' + this.translocoService.translate('general.wasNotAbleGenImage', {}, this.translocoService.getActiveLang()));
                        this.clearFilterSelection();
                        this.loadingImages = false;
                        this.cd.markForCheck();
                        return;
                    }
                    this.aiImages = reply.payload
                        .map((item, index) => {
                            if (item?.url) { // DALL·E-3 (URL) variant
                                return {
                                    name: this.extractImageName(item.url),
                                    url: item.url,
                                    base64: '',
                                    loading: true, // Needs blob download
                                };
                            } else if (item?.b64_json) { // GPT-image (Base64) variant
                                return {
                                    name: `ai_image_${Date.now()}_${index}.png`, // More unique name
                                    url: '', // No URL needed for download
                                    base64: `data:image/png;base64,${item.b64_json}`,
                                    loading: false, // Already have data
                                };
                            } else if (item?.imageData && item?.mimeType) { // New format with imageData and mimeType
                                const extension = item.mimeType.split('/')[1] || 'png';
                                return {
                                    name: `ai_image_${Date.now()}_${index}.${extension}`,
                                    url: '',
                                    base64: `data:${item.mimeType};base64,${item.imageData}`,
                                    loading: false, // Already have data
                                };
                            }
                            console.warn("Received unexpected item format in DALL-E reply:", item);
                            return null; // Ignore invalid items
                        })
                        .filter(img => img !== null) as IAIImage[]; // Filter out nulls and assert type

                    const firstImageToLoad = this.aiImages.find(img => img.loading && img.url);
                    if (firstImageToLoad) {
                        this.storeSelector.dispatch(actionGetAIImage({prompt: firstImageToLoad.url}));
                    } else {
                        // No images need downloading (all were base64 or none were valid)
                        this.loadingImages = false; // Ensure loading is off if no further action needed
                    }
                    this.loadingImages = false;
                    this.cd.markForCheck();
                })
        );
    }

    private subscribeToDalleImageBlobReply(): void {
        this.cod(
            this.actionsSubj
                .pipe(ofType(actionGetAIImageReply))
                .subscribe((reply: any) => { // Use 'any' if type isn't well-defined, or create an interface
                    // Validate the received blob payload
                    if (!reply.url || !reply.payload || !(reply.payload instanceof Blob)) {
                        console.error("Received invalid DALL-E image blob reply:", reply);
                        // Attempt to mark the corresponding image as not loading to prevent UI freeze
                        if (reply.url) {
                            const imgWithError = this.aiImages.find(i => i.url === reply.url);
                            if (imgWithError) {
                                imgWithError.loading = false;
                            }
                        }
                        // Check if any other images are still loading
                        if (!this.aiImages.some(i => i.loading)) {
                            this.loadingImages = false;
                        }
                        this.cd.markForCheck();
                        return;
                    }

                    const blob = reply.payload as Blob;
                    const imageUrl = reply.url; // URL identifies which image this blob is for
                    const reader = new FileReader();

                    reader.onload = (e) => {
                        const targetImage = this.aiImages.find(img => img.url === imageUrl);
                        if (targetImage) {
                            targetImage.base64 = e.target?.result as string ?? ''; // Get base64 data
                            targetImage.loading = false; // Mark as loaded
                        } else {
                            console.warn("Received blob for an unknown image URL:", imageUrl);
                        }

                        // Find the *next* image that still needs loading
                        const nextImageToLoad = this.aiImages.find(img => img.loading && img.url);
                        if (nextImageToLoad) {
                            this.storeSelector.dispatch(actionGetAIImage({prompt: nextImageToLoad.url}));
                        } else {
                            // No more images are loading
                            this.loadingImages = false;
                        }
                        this.cd.markForCheck();
                    };

                    reader.onerror = (error) => {
                        console.error("FileReader error reading image blob:", error);
                        const targetImage = this.aiImages.find(img => img.url === imageUrl);
                        if (targetImage) {
                            targetImage.loading = false; // Stop loading on error
                        }
                        // Check if other images still need loading
                        if (!this.aiImages.some(i => i.loading)) {
                            this.loadingImages = false;
                        }
                        this.cd.markForCheck();
                    };

                    reader.readAsDataURL(blob); // Start reading the blob
                })
        );
    }

    private subscribeToVeroVideoReply(): void {
        this.cod(
            this.actionsSubj
                .pipe(ofType(actionGetAIVideoReply))
                .subscribe((payload: any) => { // Use 'any' or define type
                    this.loadingImages = false; // Turn off general loading indicator
                    if (payload?.reply?.error) {
                        this.toastr.error(
                            payload.reply.error,
                            'creation failed',
                            {timeOut: 0, closeButton: true}
                        );
                        return;
                    }
                    if (_.isEmpty(payload.reply)) {
                        // Handle upgrade prompts or empty payloads specifically
                        return this.toastr.error(this.translocoService.translate('general.pleaseUpgrade3', {}, this.translocoService.getActiveLang()));
                    }

                    if (payload?.reply?.videoUri) {
                        const receivedVideoUrl = payload.reply.videoUri;
                        // Basic URL validation
                        try {
                            new URL(receivedVideoUrl); // Throws error if invalid
                            this.videoUrl = receivedVideoUrl;
                            this.safeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.videoUrl);
                            this.videoUploadReady = true;
                            this.toastr.info('Video created successfully. Ready for upload.', '', TOAST_FASTER);
                        } catch (e) {
                            console.error("Received invalid video URL:", receivedVideoUrl, e);
                            this.toastr.error("Received an invalid URL for the video.", 'Video Creation Failed');
                            this.resetVideoState();
                        }
                    } else {
                        const errorMsg = payload?.reply?.error || 'Unknown error creating video';
                        this.toastr.error(`Video Creation Failed: ${errorMsg}`);
                        this.resetVideoState();
                    }
                    this.cd.markForCheck();
                })
        );
    }

    private subscribeToUploadCompletion(): void {
        this.cod(
            this.appService.getUploadingFiles().subscribe((uploadingFiles: FileObj[] | null) => {
                if (!uploadingFiles || uploadingFiles.length === 0) return;

                // Find the first newly completed file not yet processed
                const newlyCompleted = uploadingFiles.find(f =>
                    f.uploadStatus === UploadStatus.SUCCESS &&
                    f.fileId &&
                    !this.processedFileIds.has(f.fileId)
                );

                if (newlyCompleted) {
                    console.log(`Processing completed upload: ${newlyCompleted.fileName} (ID: ${newlyCompleted.fileId})`);
                    this.processedFileIds.add(newlyCompleted.fileId); // Mark as processed *before* async operation
                    this.createResourceByUploading(newlyCompleted); // Trigger resource creation in backend
                }

                // --- Cleanup processedFileIds set ---
                const currentFileIdsInService = new Set(
                    uploadingFiles.map(f => f.fileId).filter(id => !!id)
                );
                this.processedFileIds.forEach(processedId => {
                    if (!currentFileIdsInService.has(processedId)) {
                        console.log(`Removing obsolete processed ID: ${processedId}`);
                        this.processedFileIds.delete(processedId); // Remove IDs no longer relevant
                    }
                });
            })
        );
    }


    // --- UI Event Handlers ---

    // Called when switching between Image/Video generation
    onSelection(e: any): void { // Use MatRadioChange event type if applicable
        const value = e.value;
        if (value === 'image' || value === 'video') {
            this.clearFilterSelection(); // Clear previous results
            this.selectedMediaType = value;
            this.filter = (value === 'image') ? this.filter1 : this.filter2; // Set example prompt
            this.cd.markForCheck();
        }
    }

    // Called when image size/type selection changes (if applicable)
    onFilterSelection(e: any): void { // Use MatSelectChange type if applicable
        const value = e?.value;
        // Assuming the value directly holds the size string like '1024x1024'
        if (typeof value === 'string' && value.length > 1) {
            this.m_enumMediaTypeSelected = value;
            console.log(`Image size selected: ${this.m_enumMediaTypeSelected}`);
            this.cd.markForCheck();
        }
    }


    // --- Core Actions ---

    // Trigger AI generation (Image or Video)
    getImages(): void {
        // 1. Check Call Limit
        if (!this.checkAICallLimit()) {
            return; // Limit reached, toast shown inside check function
        }

        // 2. Check Account Status/Permissions
        if (!this.user) {
            this.toastr.error("User data not available. Please reload.", "Error");
            return;
        }
        if (Lib.IsAccessBlocked(this.user)) {
            this.toastr.error(this.translocoService.translate('account.screenDisruption', {}, this.translocoService.getActiveLang()), '[ Contact live chat ]');
            return;
        }
        // This check might be redundant if checkAICallLimit handles non-dev,
        // but keep if 'enterprise' has special privileges.
        // if (!this.user.enterprise && !isHostDevOrLocal()) {
        //     this.routeToUpgrade();
        //     return;
        // }

        // 3. Validate Input
        const trimmedFilter = this.filter?.trim();
        if (!trimmedFilter) {
            this.toastr.warning("Please enter a description for the AI first.");
            return;
        }

        // 4. Prepare UI and Dispatch Action
        console.log(`Initiating AI ${this.selectedMediaType} generation with prompt: "${trimmedFilter}"`);
        this.loadingImages = true;
        this.aiImages = []; // Clear previous images
        this.resetVideoState(); // Clear previous video
        this.cd.markForCheck(); // Update UI immediately

        if (this.selectedMediaType === 'image') {
            this.storeSelector.dispatch(actionGetAIImages({prompt: trimmedFilter, size: this.m_enumMediaTypeSelected}));
        } else { // 'video'
            this.storeSelector.dispatch(actionGetAIVideo({prompt: trimmedFilter}));
        }
    }

    // Upload a selected generated image
    async uploadImageToResource(selectedImage: IAIImage): Promise<void> {
        if (!selectedImage || !selectedImage.base64) {
            this.toastr.error("Cannot upload - image data is missing.", '', TOAST_FASTER);
            return;
        }

        this.toastr.info("Preparing image for upload...", '', TOAST_FASTER);
        this.appService.setUploadingStatus({process: 0, show: true}); // Show progress indicator

        try {
            // Use a more robust name if 'name' isn't guaranteed unique or valid
            const fileName = selectedImage.name || `ai_image_${Date.now()}`;
            const fileInfo = await convertToFileFromImageUrlOrBase64(selectedImage.base64, fileName);

            if (!fileInfo || !fileInfo.fileObj) {
                throw new Error("Could not convert image data to a file object.");
            }

            const fileToUpload: FileObj = {
                fileName: fileInfo.fileName,
                fileType: fileInfo.fileType,
                file: fileInfo.fileObj,
                fileId: `${fileInfo.fileName}-${fileInfo.fileObj.lastModified || Date.now()}-${Math.random().toString(36).substring(2, 9)}`, // Add randomness for better uniqueness
                progress: 0,
                uploadStatus: UploadStatus.READY,
                formattedSize: formatBytes(fileInfo.fileObj.size, 2),
                fileSize: fileInfo.fileObj.size
            };

            this.appService.setUploadingFiles([fileToUpload]); // Add to upload queue
            this.appService.uploadResourceFiles(); // Start the upload process (async)

        } catch (error: any) {
            console.error("Error preparing image for upload:", error);
            this.toastr.error(`Failed to prepare image: ${error.message || 'Unknown error'}`, '', TOAST_FASTER);
            this.appService.setUploadingStatus({process: 0, show: false}); // Hide progress on error
        }
    }

    // Upload the generated video
    async uploadVideoToResource(): Promise<void> {
        if (!this.videoUrl || !this.videoUploadReady) {
            this.toastr.error("No video ready for upload.", '', TOAST_FASTER);
            return;
        }

        this.toastr.info("Preparing video for upload...", '', TOAST_FASTER);
        this.appService.setUploadingStatus({process: 0, show: true});

        try {
            let potentialName = 'ai_video';
            try { // Safely extract name from URL
                const urlObj = new URL(this.videoUrl);
                const pathParts = urlObj.pathname.split('/');
                const lastPart = pathParts.pop()?.split('?')[0]; // Remove query params
                if (lastPart) {
                    const dotIndex = lastPart.lastIndexOf('.');
                    potentialName = dotIndex > 0 ? lastPart.substring(0, dotIndex) : lastPart;
                }
            } catch (e) {
                console.warn("Could not parse video URL for filename", e);
            }

            const uniqueFileName = `${potentialName}_${Date.now()}`;
            const fileInfo = await this.convertToFileFromUrl(this.videoUrl, uniqueFileName);

            const fileToUpload: FileObj = {
                fileName: fileInfo.fileName,
                fileType: fileInfo.fileType,
                file: fileInfo.fileObj,
                fileId: `${fileInfo.fileName}-${fileInfo.fileObj.lastModified || Date.now()}-${Math.random().toString(36).substring(2, 9)}`, // Add randomness
                progress: 0,
                uploadStatus: UploadStatus.READY,
                formattedSize: formatBytes(fileInfo.fileObj.size, 2),
                fileSize: fileInfo.fileObj.size
            };

            this.appService.setUploadingFiles([fileToUpload]);
            this.appService.uploadResourceFiles(); // Start upload (async)

        } catch (error: any) {
            console.error("Error preparing video for upload:", error);
            this.toastr.error(`Failed to prepare video: ${error.message || 'Unknown error'}`, '', TOAST_FASTER);
            this.appService.setUploadingStatus({process: 0, show: false});
        }
    }

    // --- Helper Functions ---

    // Clears generated content and resets UI state
    clearFilterSelection(): void {
        this.filter = (this.selectedMediaType === 'image') ? this.filter1 : this.filter2; // Reset to example prompt
        this.aiImages = [];
        this.resetVideoState();
        this.loadingImages = false;
        this.cd.markForCheck();
    }

    // Resets video-specific state
    private resetVideoState(): void {
        this.safeVideoUrl = null;
        this.videoUrl = null;
        this.videoUploadReady = false;
    }

    // Checks and updates the daily AI call limit
    private checkAICallLimit(): boolean {
        if (isHostDevOrLocal()) {
            console.log("AI Call Limit Check: Bypassed (local/dev environment).");
            // return true; // Always allow in dev/local
        }

        const today = new Date();
        // Normalize today's date to midnight for reliable comparison
        const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        let callDateNormalized: Date | null = null;
        // Check if the stored callDate is a valid Date object
        if (this.callDate instanceof Date && !isNaN(this.callDate.getTime())) {
            // Normalize the stored call date to midnight
            callDateNormalized = new Date(this.callDate.getFullYear(), this.callDate.getMonth(), this.callDate.getDate());
        }

        // --- Logic for checking/resetting/incrementing ---
        if (!callDateNormalized || callDateNormalized < todayNormalized) {
            // Condition: No valid date stored OR stored date is from a previous day
            console.log("AI Call Limit Check: New day or first call detected. Resetting count.");
            this.localStore.setStorageValConfigGroup([
                {key: 'resourceAICallDate', val: today.toISOString()},  // Store full timestamp
                {key: 'resourceAICalls', val: '1'}  // Start count at 1
            ]);
            this.callDate = today; // Update component state
            this.callsToday = 1;
            this.cd.markForCheck();
            return true; // Allow the first call of the day
        } else {
            // Condition: It's the same day (callDateNormalized === todayNormalized)
            if (this.callsToday >= this.totalCalls) {
                // Limit reached for today
                console.warn(`AI Call Limit Check: Limit reached (${this.callsToday}/${this.totalCalls}).`);
                this.toastr.warning(this.translocoService.translate('toast.limitedAICalls', {count: this.totalCalls}, this.translocoService.getActiveLang()));
                this.cd.markForCheck(); // Update UI just in case
                return false; // Disallow call
            } else {
                // Limit not reached, increment count
                this.callsToday += 1;
                this.localStore.setStorageValConfig('resourceAICalls', this.callsToday.toString());
                console.log(`AI Call Limit Check: Call allowed. Count updated to ${this.callsToday}/${this.totalCalls}.`);
                this.cd.markForCheck();
                return true; // Allow call
            }
        }
    }

    // Navigates user to upgrade page/dialog
    private routeToUpgrade(): void {
        this.appService.openWizardUpgradeDialog(EnumWizardUpgradeToasts.UPGRADE_TO_USE_FEATURE);
    }

    // Creates backend resource record after file upload succeeds
    private createResourceByUploading(uploadedFile: FileObj): void {
        // Guard clause: Ensure we have necessary data
        if (!uploadedFile?.fileId || !uploadedFile.fileName || uploadedFile.uploadStatus !== UploadStatus.SUCCESS) {
            console.error("createResourceByUploading: Invalid FileObj received.", uploadedFile);
            this.toastr.error("Failed to finalize upload due to missing file details.", '', TOAST_FASTER);
            this.appService.setUploadingStatus({process: 0, show: false}); // Hide progress
            // Attempt to remove from processed set if ID exists
            if (uploadedFile?.fileId) this.processedFileIds.delete(uploadedFile.fileId);
            return;
        }

        console.log(`Creating resource record for: ${uploadedFile.fileName}`);
        const resourceName = uploadedFile.fileName;
        // Ensure resourceType has a fallback if fileType is missing/undefined
        const resourceType = uploadedFile.fileType?.toLowerCase() ?? 'unknown';

        const resourceData = {
            resource_name: resourceName,
            resource_type: resourceType,
            resource_total_time: '0', // Default, adjust if applicable (e.g., for video duration later)
            default_player: this.storeSelector.getBlockLabelByFileType(resourceType)?.id as any || 'default', // Use fallback
            resource_date_created: Date.now().toString(), // Use timestamp number converted to string
            resource_date_modified: Date.now().toString(),
            resource_bytes_total: (uploadedFile.fileSize ?? 0).toString(), // Use fallback
            access_key: '0', // Default access key
            tree_path: '' // Default tree path
        };

        const saveRecord: ICRUDRecord = {
            studioId: this.localStore.getStorageConfig()?.studioId, // Safe navigation
            command: 'update', // Or 'create' depending on backend logic for associating fileId with resource
            primary: 'resource_id',
            table: 'resources',
            body: resourceData,
            id: uploadedFile.fileId // The fileId links the uploaded file to this resource record
        };

        if (!saveRecord.studioId) {
            console.error("createResourceByUploading: Missing studioId.");
            this.toastr.error("Configuration error: Studio ID is missing.", '', TOAST_FASTER);
            this.appService.setUploadingStatus({process: 0, show: false});
            this.processedFileIds.delete(uploadedFile.fileId); // Clean up processed ID
            return;
        }

        this.appService.crudRecord(saveRecord)
            .then((i_crudReply: ICRUDRecord | null) => { // Handle potential null reply
                if (!i_crudReply?.serverReply?.status) {
                    console.error("Failed to update/create resource metadata:", i_crudReply?.serverReply);
                    this.toastr.error(this.translocoService.translate('toast.imageUploadFailed', {}, this.translocoService.getActiveLang()), '', TOAST_FASTER); // More generic message
                    // Don't hide progress immediately, let user see the error
                    // this.appService.setUploadingStatus({ process: 0, show: false });
                    this.processedFileIds.delete(uploadedFile.fileId); // Allow retry if needed by removing from set
                    return;
                }

                // Success: Add resource to the store
                // Ensure server reply contains necessary IDs
                const resourceId = String(uploadedFile.fileId); // Assuming resource_id is the fileId
                const changelistId = String(i_crudReply.serverReply.changelist_id ?? '');

                // Construct the full resource object for the store
                const finalResource: IResource = {
                    ...resourceData,
                    resource_id: resourceId,
                    id: resourceId, // Often id and resource_id are the same
                    changelist_id: changelistId,
                    // Ensure all required fields for IResource are present
                } as IResource; // Assert type carefully

                this.storeSelector.dispatch(actionAddResources({data: finalResource}));
                this.toastr.info(this.translocoService.translate('toast.successUpload', {}, this.translocoService.getActiveLang()), '', TOAST_FASTER);

                // Hide progress indicator after a short delay
                setTimeout(() => {
                    this.appService.setUploadingStatus({process: 0, show: false});
                }, 1500);

            })
            .catch(error => {
                console.error("Error during resource metadata update/creation:", error);
                this.toastr.error("Failed to save resource details after upload.", '', TOAST_FASTER);
                this.appService.setUploadingStatus({process: 0, show: false});
                this.processedFileIds.delete(uploadedFile.fileId); // Clean up processed ID on catch
            });
    }

    // Extracts a filename (without extension) from a URL
    private extractImageName(url: string): string {
        if (!url) return `ai_image_${Date.now()}`;
        try {
            // Get the last part of the path, remove query string
            const pathPart = url.split('?')[0].split('/').pop() ?? '';
            // Remove extension if present
            const dotIndex = pathPart.lastIndexOf('.');
            const name = dotIndex > 0 ? pathPart.substring(0, dotIndex) : pathPart;
            // Return the extracted name or a default if empty
            return name || `ai_image_${Date.now()}`;
        } catch (e) {
            console.warn("Could not parse URL to extract image name:", e);
            return `ai_image_${Date.now()}`; // Fallback
        }
    }

    // Downloads content from a URL and converts it to a File object
    private async convertToFileFromUrl(url: string, filename: string): Promise<{ fileObj: File; fileType: string; fileName: string }> {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}`);
            }
            const blob = await response.blob();

            // Determine file type and extension more robustly
            const fileType = blob.type || 'application/octet-stream';
            let fileExtension = '';

            // Basic mime type to extension mapping
            const commonTypes: { [key: string]: string } = {
                'video/mp4': 'mp4', 'video/webm': 'webm', 'video/ogg': 'ogv',
                'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp',
                'application/pdf': 'pdf',
                // Add more as needed
            };

            fileExtension = commonTypes[fileType] || fileType.split('/')[1] || ''; // Fallback to subtype

            // Construct filename with extension
            const nameWithoutExtension = filename.includes('.') ? filename.substring(0, filename.lastIndexOf('.')) : filename;
            const finalFilename = fileExtension ? `${nameWithoutExtension}.${fileExtension}` : filename; // Add extension if found

            const fileObj = new File([blob], finalFilename, {type: fileType});

            console.log(`Converted URL to File: Name=${finalFilename}, Type=${fileType}, Size=${fileObj.size}`);
            // Return file type as the determined extension or the second part of mime type
            return {fileObj, fileType: fileExtension || fileType.split('/')[1] || 'bin', fileName: finalFilename};

        } catch (error: any) {
            console.error('Error fetching file from URL:', url, error);
            throw new Error(`Failed to download file: ${error.message || 'Network request failed'}`);
        }
    }

    // For ngFor optimization in the template
    trackByName: TrackByFunction<IAIImage> = (index: number, img: IAIImage): string => {
        return img.name || img.url || index.toString(); // Use name, URL, or index as fallback key
    };

    // --- Cleanup ---
    override destroy(): void {
        super.destroy(); // Call base class cleanup (handles subscriptions added via `cod`)
        // Additional cleanup specific to this component
        console.log("Destroying ResourcesAi component, cleaning up...");
        this.appService.setUploadingFiles([]); // Clear any active uploads in the service state if appropriate
        this.appService.setUploadingStatus({process: 0, show: false}); // Ensure upload status is hidden
        this.processedFileIds.clear(); // Clear the set of processed IDs
    }

    // Explicitly implement OnDestroy if not handled by Compbaser's `destroy`
    override ngOnDestroy(): void {
        this.destroy(); // Call the cleanup method
    }
}
